import type { OptionDB } from "@project/emerge";
import { TRAY_PANEL_CODES_BY_THICKNESS } from "@project/pricing";
import { LTCaseUserOption } from "../../enum/LTCaseUserOption";
import { PaddingStyle } from "../../enum/PaddingStyle";
import { buildTree, calculateTree } from "../../genetic_optimize/ContentTree";
import type { CaseInteriorOptions } from "../../interfaces/AccessoryCompartmentOptions";
import type { CaseCompartment } from "../../interfaces/CaseCompartment";
import type { ContentItem } from "../../interfaces/ContentItem";
import {
  type PanelColor,
  PanelMaterialCategory,
} from "../../interfaces/PanelColor";
import type { ProfileType } from "../../interfaces/ProfileType";
import type { Root } from "../../interfaces/Root";
import type { TrayMaterialType } from "../../interfaces/TrayMaterialType";

// Constants for clean cutting and milling
export const CLEANCUT = 100; // make cutouts larger by this for clean cutting
export const MILLING_EXTRA = 3; // make cutouts larger by half milling

/**
 * Whether a centred accessory compartment can actually be built.
 *
 * The contents have to part at the centre line to leave room for it. A
 * compartment that spans the centre cannot, so the compartment falls back to
 * the wall placement. The blueprint and the build step must agree on this —
 * growing the case for two dividers while only placing one leaves a gap the
 * width of a divider.
 */
export function resolveAccessoryPlacement(
  accessoryCompartment: { placement?: "WALL" | "CENTER" } | undefined,
  volumes: ReadonlyArray<Pick<CaseCompartment, "center" | "sizeWithPadding">>,
  axisIndex: number,
): "WALL" | "CENTER" {
  if (accessoryCompartment?.placement !== "CENTER") return "WALL";
  if (volumes.length === 0) return "WALL";

  let hasNegativeSide = false;
  let hasPositiveSide = false;
  for (const volume of volumes) {
    const half = volume.sizeWithPadding.getComponent(axisIndex) / 2;
    const centre = volume.center.getComponent(axisIndex);
    if (Math.abs(centre) < half) return "WALL"; // spans the centre line
    if (centre < 0) hasNegativeSide = true;
    else hasPositiveSide = true;
  }

  // Content on both sides means a divider already sits on the centre line; the
  // compartment's own two walls replace it.
  return hasNegativeSide && hasPositiveSide ? "CENTER" : "WALL";
}

/**
 * Whether a centred accessory compartment can be placed for the current layout
 * along the given axis (0 = x / längs, 2 = z / quer).
 */
export function canPlaceCenteredAccessoryCompartment(
  optionDb: OptionDB,
  interior: CaseInteriorOptions,
  axisIndex: number,
): boolean {
  const trayMaterial = getTrayMaterialWithFallback(
    optionDb,
    interior.trayMaterial,
    "canPlaceCenteredAccessoryCompartment",
  );
  if (!trayMaterial) return false;

  const tolerance = getToleranceWithFallback(
    interior.tolerance,
    "canPlaceCenteredAccessoryCompartment",
  );

  const calculationResult = calculatePanelsAndVolumes(
    interior,
    trayMaterial,
    tolerance,
    "canPlaceCenteredAccessoryCompartment",
  );
  if (!calculationResult) return false;

  return (
    resolveAccessoryPlacement(
      { placement: "CENTER" },
      calculationResult.volumes,
      axisIndex,
    ) === "CENTER"
  );
}

// Hilfsfunktionen für konsistente Defaults
/**
 * Returns a hard-coded light brown color for interior panels
 * Color:hsl(34, 75.20%, 26.90%) (a light brown, lighter than tan)
 */
export function getDefaultPanelColor(): PanelColor {
  return {
    name: "Hellbraun (Default)",
    description: "Hellbraun für Innenplatten",
    color: "#784b11", // light brown color
    boardSize: [2500, 1250],
    materialCategory: PanelMaterialCategory.BirchMultiplex,
    codesByThickness: { ...TRAY_PANEL_CODES_BY_THICKNESS },
    kind: LTCaseUserOption.PanelColor,
    deleted: false,
    descriptionEn: "",
    availableExternally: false,
  };
}

export function ensureStrength(
  strength: number | undefined,
  material?: TrayMaterialType,
): number {
  // Verwende strength falls vorhanden (auch wenn 0 - kann valide sein)
  if (strength !== undefined) {
    return strength;
  }
  // Sonst verwende material.strength falls vorhanden
  if (material?.strength !== undefined) {
    return material.strength;
  }
  // Nur als letzter Ausweg: Default 5mm
  return 5;
}

export function calculateProfileOffset(
  profiles: ProfileType,
  upperPaddingAlignment: boolean,
  overHead = false,
): number {
  if (!upperPaddingAlignment) {
    return -profiles.height;
  }
  // Original logic - check if name comparison is robust enough
  const isSTW1384 = (profiles as { name?: string }).name === "STW1384";
  if ((profiles.inverted !== overHead) !== isSTW1384) {
    return profiles.strength;
  }
  return profiles.extra - profiles.strength;
}

export function getVector3Index(axis: "x" | "y" | "z"): number {
  switch (axis) {
    case "x":
      return 0;
    case "y":
      return 1;
    case "z":
      return 2;
    default:
      return 0;
  }
}

export function getTrayMaterialWithFallback(
  optionDb: OptionDB,
  trayMaterial: TrayMaterialType | undefined,
  buildstepName: string,
): TrayMaterialType | null {
  if (trayMaterial) return trayMaterial;

  console.warn(
    `[${buildstepName}] Missing trayMaterial in settings for CASE interior. Using default.`,
  );
  try {
    const defaultMaterial = optionDb.getOrThrow(
      LTCaseUserOption.TrayMaterialType,
      { name: "PLW1415" },
    ) as TrayMaterialType;
    return defaultMaterial;
  } catch {
    console.error(
      `[${buildstepName}] Could not load default trayMaterial. Aborting.`,
    );
    return null;
  }
}

/**
 * Tray material for a given thickness. Falls back to the closest available
 * thickness instead of throwing: seeded strengths (6.5, 9, 12, …) rarely match
 * a computed underlay thickness exactly, and an unpriced panel beats no build.
 */
export function resolveTrayMaterialByStrength(
  optionDb: OptionDB,
  strength: number,
): TrayMaterialType {
  const options = optionDb.getOptions<TrayMaterialType>(
    LTCaseUserOption.TrayMaterialType,
  );
  const exact = options?.find((option) => option.strength === strength);
  if (exact) return exact;

  const closest = options?.reduce((best, option) =>
    Math.abs(option.strength - strength) < Math.abs(best.strength - strength)
      ? option
      : best,
  );
  if (!closest) {
    throw new Error(`No tray material available for strength ${strength}`);
  }
  console.warn(
    `No tray material with strength ${strength}mm; using ${closest.name} (${closest.strength}mm).`,
  );
  return closest;
}

export function getToleranceWithFallback(
  tolerance: { x: number; y: number; z: number } | undefined,
  buildstepName: string,
): { x: number; y: number; z: number } {
  if (tolerance) return tolerance;

  console.warn(
    `[${buildstepName}] Missing tolerance in settings for CASE interior. Using default.`,
  );
  return { x: 7, y: 2, z: 7 };
}

export function calculateFromTree(
  tree: Root,
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  _buildstepName: string,
  defaultPaddingStyle: PaddingStyle = PaddingStyle.STANDARD,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } {
  const result = calculateTree(
    tree,
    trayMaterial.strength,
    tolerance,
    defaultPaddingStyle,
  );
  return { panels: result.panels, volumes: result.volumes, mode: "TREE" };
}

export function calculateFromContents(
  contents: ContentItem[],
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  _buildstepName: string,
  defaultPaddingStyle: PaddingStyle = PaddingStyle.STANDARD,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } {
  const convertedTree = buildTree(contents);
  const result = calculateTree(
    convertedTree,
    trayMaterial.strength,
    tolerance,
    defaultPaddingStyle,
  );
  return { panels: result.panels, volumes: result.volumes, mode: "CONTENTS" };
}

export function calculatePanelsAndVolumes(
  interior: CaseInteriorOptions,
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  buildstepName: string,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } | null {
  const { tree, contents } = interior;
  const defaultPaddingStyle = interior.paddingStyle ?? PaddingStyle.STANDARD;

  if (tree) {
    return calculateFromTree(
      tree,
      trayMaterial,
      tolerance,
      buildstepName,
      defaultPaddingStyle,
    );
  }
  if (contents && contents.length > 0) {
    return calculateFromContents(
      contents,
      trayMaterial,
      tolerance,
      buildstepName,
      defaultPaddingStyle,
    );
  }
  return null;
}
